// UserMenu.jsx
import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, ChevronDown, UserCircle } from 'lucide-react';

// Avatar button and image
const MenuTrigger = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.black};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const AvatarImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.offWhiteDark};
  border: 2px solid ${({ theme }) => theme.colors.white};
  transition: all 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    color: ${({ theme }) => theme.colors.primaryDark};
  }

  ${MenuTrigger}:hover & {
    transform: scale(1.05);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

// Dropdown components
const MenuContainer = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-radius: 8px;
  overflow: hidden;
  min-width: 220px;
  z-index: ${({ theme }) => theme.zIndices.dropdown};
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const UserInfo = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.offWhiteDark};
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primaryDark};
  word-break: break-all;
`;

const MenuItem = styled(Link)`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background-color 0.2s ease;
  font-size: 14px;
  text-decoration: none;
  color: inherit;

  svg {
    color: ${({ theme }) => theme.colors.primaryDark};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.offWhite};
  }
`;

const MenuButton = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background-color 0.2s ease;
  font-size: 14px;
  cursor: pointer;

  svg {
    color: ${({ theme }) => theme.colors.primaryDark};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.offWhite};
  }
`;

const UserMenu = ({ user, logout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Get display name and email
  const displayName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const email = user?.email || '';

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    logout();
    navigate('/login');
  };

  return (
    <MenuContainer ref={menuRef}>
      <MenuTrigger
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="User menu"
      >
        <AvatarImage>
          {user?.photoURL ? (
            <img src={user.photoURL} alt={`${displayName}'s avatar`} />
          ) : (
            <UserCircle size={24} />
          )}
        </AvatarImage>
        <ChevronIcon isOpen={isOpen}>
          <ChevronDown size={16} />
        </ChevronIcon>
      </MenuTrigger>

      {isOpen && (
        <Dropdown role="menu">
          <UserInfo>
            <UserName>{displayName}</UserName>
            <UserEmail>{email}</UserEmail>
          </UserInfo>

          <MenuItem to="/admin" onClick={() => setIsOpen(false)}>
            <Settings size={18} />
            Dashboard
          </MenuItem>

          <MenuItem to="/admin?tab=Profile" onClick={() => setIsOpen(false)}>
            <User size={18} />
            My Profile
          </MenuItem>

          <MenuButton onClick={handleLogout}>
            <LogOut size={18} />
            Sign Out
          </MenuButton>
        </Dropdown>
      )}
    </MenuContainer>
  );
};

export default UserMenu;
